import { parseISO, isValid } from 'date-fns'
import jwtDecode from 'jwt-decode'
import { isNil } from 'lodash-es'
import { AccessToken, Locale } from '@/types'

import format from '@/helpers/format'
import { useUserSetting } from '@/hooks'

export type Options = {
  showTime?: boolean
  timeOnly?: boolean
  includeSecondsInTime?: boolean
  includeMinutesInTime?: boolean
  customLocale?: Locale
  intlOptions?: Intl.DateTimeFormatOptions
}

const defaultOptions: Options = {
  showTime: false,
  timeOnly: false,
  includeSecondsInTime: false,
  includeMinutesInTime: true,
}

const useLocaleFormattedDate = () => {
  const [{ value: preferredDateFormat, loading }] = useUserSetting<string>(
    'PREFERRED_DATE_FORMAT',
    'default',
    {
      fetchPolicy: 'cache-first',
    },
  )

  return (
    date: Date | string | number | null | undefined,
    options?: Options,
  ) => {
    const optionsToUse = {
      ...defaultOptions,
      ...options,
    }

    if (!date || loading) {
      return null
    }

    if (typeof date === 'string') {
      date = parseISO(date)
    } else if (typeof date === 'number') {
      date = new Date(date)
    }

    if (!isValid(date)) {
      return null
    }

    const {
      showTime,
      timeOnly,
      includeSecondsInTime,
      includeMinutesInTime,
      customLocale,
      intlOptions,
    } = optionsToUse

    if (
      !intlOptions &&
      preferredDateFormat &&
      preferredDateFormat !== 'default' &&
      isNil(customLocale)
    ) {
      let timePart = ''

      if (showTime || timeOnly) {
        timePart = includeSecondsInTime ? 'HH:mm:ss' : 'HH:mm'
      }

      if (timeOnly) {
        return format(date, timePart)
      }

      return format(
        date,
        `${preferredDateFormat}${timePart.length ? `, ${timePart}` : ''}`,
      )
    }

    const parsedIntlOptions: Intl.DateTimeFormatOptions = intlOptions ?? {
      year: !timeOnly ? 'numeric' : undefined,
      month: !timeOnly ? 'numeric' : undefined,
      day: !timeOnly ? 'numeric' : undefined,
      hour: showTime || timeOnly ? 'numeric' : undefined,
      minute:
        (showTime || timeOnly) && includeMinutesInTime ? 'numeric' : undefined,
      second:
        (showTime || timeOnly) && includeSecondsInTime ? 'numeric' : undefined,
    }

    const locale = (
      customLocale ??
      jwtDecode<AccessToken>(sessionStorage.getItem('accessToken') as string)
        ?.user?.locale ??
      'en_US'
    ).replace('_', '-')

    return new Intl.DateTimeFormat(locale, parsedIntlOptions).format(date)
  }
}

export default useLocaleFormattedDate
